/**
 * G2eGente - Página Pública de Vaga
 * Permite visualizar detalhes da vaga sem autenticação
 * URL: /vagas/{job_id}
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  Briefcase,
  MapPin,
  Clock,
  Building2,
  ArrowRight,
  Loader2,
  CheckCircle,
  Calendar,
  Users,
  FileText,
  ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicJobPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`${API}/api/jobs/${jobId}/public`);
      setJob(response.data.job);
      setCompany(response.data.company);
    } catch (err) {
      setError('Vaga não encontrada ou não está mais disponível.');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    // Store job ID to redirect after login/register
    localStorage.setItem('apply_job_id', jobId);
    navigate('/m/register');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Ops!</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/m/login')} className="bg-cyan-600 hover:bg-cyan-700">
              Acessar Plataforma
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">G2</span>
              </div>
              <span className="font-bold text-xl text-slate-900">G2eGente</span>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/m/login')}
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            >
              Já tenho conta
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Job Card */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold mb-2">{job?.titulo}</h1>
                <div className="flex flex-wrap gap-4 text-cyan-100">
                  {company?.nome && (
                    <div className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      <span>{company.nome}</span>
                    </div>
                  )}
                  {job?.localizacao && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{job.localizacao}</span>
                    </div>
                  )}
                  {job?.tipo_contrato && (
                    <div className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      <span>{job.tipo_contrato}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <CardContent className="p-6">
            {/* Status Badge */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Vaga Ativa
              </Badge>
              {job?.destaque && (
                <Badge className="bg-amber-100 text-amber-700">
                  ⭐ Vaga em Destaque
                </Badge>
              )}
            </div>

            {/* Description */}
            <div className="mb-6">
              <h2 className="font-semibold text-slate-900 mb-3">Descrição da Vaga</h2>
              <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">
                {job?.descricao || 'Descrição não disponível.'}
              </p>
            </div>

            {/* Requirements - if DISC is configured */}
            {job?.perfil_ideal?.disc_desejado && (
              <div className="mb-6 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <h3 className="font-semibold text-cyan-900 mb-2">Perfil Comportamental Desejado</h3>
                <div className="flex items-center gap-2">
                  <Badge className="bg-cyan-600 text-white text-lg px-3 py-1">
                    DISC: {job.perfil_ideal.disc_desejado}
                  </Badge>
                  <span className="text-sm text-cyan-700">
                    {job.perfil_ideal.disc_desejado === 'D' && '(Dominância - Focado em resultados)'}
                    {job.perfil_ideal.disc_desejado === 'I' && '(Influência - Sociável e entusiasta)'}
                    {job.perfil_ideal.disc_desejado === 'S' && '(Estabilidade - Paciente e confiável)'}
                    {job.perfil_ideal.disc_desejado === 'C' && '(Conformidade - Analítico e preciso)'}
                    {job.perfil_ideal.disc_desejado === 'DI' && '(Dominante/Influente - Líder carismático)'}
                  </span>
                </div>
              </div>
            )}

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {job?.data_expiracao && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    <span>Inscrições até</span>
                  </div>
                  <p className="font-medium text-slate-900">
                    {new Date(job.data_expiracao).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Clock className="w-4 h-4" />
                  <span>Publicada em</span>
                </div>
                <p className="font-medium text-slate-900">
                  {job?.created_at ? new Date(job.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <Button 
              onClick={handleApply}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-lg py-6"
            >
              Candidatar-se a esta Vaga
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>

        {/* About Platform */}
        <Card className="border-dashed">
          <CardContent className="p-6 text-center">
            <h3 className="font-semibold text-slate-900 mb-2">Sobre o G2eGente</h3>
            <p className="text-sm text-slate-600 mb-4">
              Plataforma de recrutamento ético com foco em inclusão, consentimento e conformidade LGPD.
              Seus dados estão protegidos e você tem controle total sobre quem pode acessá-los.
            </p>
            <div className="flex justify-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Dados Protegidos
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                LGPD Compliant
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Gratuito
              </span>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-8 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-slate-500">
          © 2024 G2eGente - Marketplace de Recrutamento Ético
        </div>
      </footer>
    </div>
  );
}
